/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { lAC_ITSGetEcomCartAsync } from '../custom-actions/DataActionExtension.g';
import * as Entities from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ILAC_AuthToken } from '../custom-actions/DataServiceEntities.g';
import { refreshToken } from '../utils/authService';
import { dedupeInFlight } from '../utils/inflight';

/**
 * ITSGetEcomCart Input Action
 */
export class ITSGetEcomCartInput implements Msdyn365.IActionInput {
    public cartId: string;
    public channelId: number;
    public authTokenCookie: ILAC_AuthToken;
    public customerId: string;

    /** The cart the store's order replaces, for an order the store holds no ecommerce cart for. */
    public ecomCartId: string;

    constructor(cartId: string, channelId: number, authTokenCookie: ILAC_AuthToken, customerId: string, ecomCartId: string = '') {
        this.cartId = cartId;
        this.channelId = channelId;
        this.authTokenCookie = authTokenCookie;
        this.customerId = customerId;
        this.ecomCartId = ecomCartId;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `ITSGETECOMCART_${this.cartId}_${this.channelId}`;
    public getCacheObjectType = () => 'ITSGETECOMCART';

    // This call is how the store channel's copy of an order reaches this one, so a second call within the
    // same page has to reach the store again rather than replay the answer the first one got.
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

/**
 * The pull deletes and recreates the cart row, so two of them running at once is a collision on a
 * destructive path. Modules guard themselves with per-instance refs, which cannot see one another,
 * and the action's cache does not help because concurrent callers all miss it. Sharing the call here
 * covers every caller and every instance. The 401 retry stays inside the shared promise so a retry
 * is not duplicated either.
 */
async function getEcomCartAsync(input: ITSGetEcomCartInput, ctx: Msdyn365.IActionContext): Promise<Entities.Cart | undefined> {
    return dedupeInFlight(`ITSGetEcomCart_${input.cartId}_${input.channelId}`, async () => performGetEcomCart(input, ctx));
}

async function performGetEcomCart(input: ITSGetEcomCartInput, ctx: Msdyn365.IActionContext): Promise<Entities.Cart | undefined> {
    const authToken = input.authTokenCookie;

    if (!authToken || !authToken.IntuitDomeAccountId || !authToken.Token || !authToken.RefreshToken) {
        return undefined;
    }

    try {
        return await lAC_ITSGetEcomCartAsync(
            { callerContext: ctx },
            authToken,
            input.cartId,
            input.channelId,
            input.customerId,
            input.ecomCartId
        );
    } catch (error) {
        console.log('Error getting ecom cart:', error);

        const actionError = error as {
            data?: {
                ErrorResourceId?: string;
            };
        };

        // Anything but an expired token is reported as it came back, so callers can tell an order the store
        // no longer holds apart from a store that could not be reached.
        if (actionError.data?.ErrorResourceId !== '401') {
            throw error;
        }

        const refreshedToken = await refreshToken(ctx);
        if (!refreshedToken) {
            throw error;
        }

        return lAC_ITSGetEcomCartAsync(
            { callerContext: ctx },
            refreshedToken,
            input.cartId,
            input.channelId,
            input.customerId,
            input.ecomCartId
        );
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<Entities.Cart | undefined>>getEcomCartAsync,
    id: 'ITSGetEcomCart'
});
